/**
 * User Preferences Redux Slice
 * Manages user preferences state with async thunks for API operations
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UserPreferences } from '@/app/types/auth';
import { userPreferenceService } from '@/app/services/userPreferenceService';

/**
 * User Preferences State Interface
 */
export interface UserPreferencesState {
  preferences: UserPreferences | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastUpdated: number | null;
}

/**
 * Initial State
 */
const initialState: UserPreferencesState = {
  preferences: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastUpdated: null,
};

/**
 * Mirror preference values that non-Redux code reads from localStorage, and
 * broadcast change events so already-rendered pages refresh without reload.
 * Consolidated here so the fetch/update-single/update-multiple fulfilled cases
 * stay in sync (previously this logic was triplicated).
 *
 * Note: the datetime pattern is written to localStorage BEFORE the
 * `dateFormatChange` event fires, so listeners re-read it via
 * `getUserDateTimePattern()` — the event detail intentionally stays unchanged.
 */
function syncPreferenceSideEffects(payload: UserPreferences): void {
  if (typeof window === 'undefined') return;

  if (payload.timezone) {
    localStorage.setItem('smartmatrix_timezone', payload.timezone);
    window.dispatchEvent(new CustomEvent('timezoneChange', { detail: { timezone: payload.timezone } }));
  }

  if (payload.dateFormat) {
    localStorage.setItem('smartmatrix_dateFormat', payload.dateFormat);
    if (payload.dateTimeFormatPattern != null && payload.dateTimeFormatPattern !== '') {
      localStorage.setItem('smartmatrix_dateTimeFormatPattern', payload.dateTimeFormatPattern);
    }
    window.dispatchEvent(new CustomEvent('dateFormatChange', { detail: { dateFormat: payload.dateFormat } }));
  }
}

/**
 * Async Thunks
 */

// Fetch user preferences from API
export const fetchUserPreferences = createAsyncThunk(
  'userPreferences/fetch',
  async (userId: number, { rejectWithValue }) => {
    try {
      const preferences = await userPreferenceService.getFullUserPreferences(userId);
      return preferences;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch user preferences'
      );
    }
  }
);

// Update a single preference field
export const updateUserPreference = createAsyncThunk(
  'userPreferences/updateSingle',
  async (
    {
      userId,
      key,
      value,
    }: {
      userId: number;
      key: keyof UserPreferences;
      value: UserPreferences[keyof UserPreferences];
    },
    { rejectWithValue }
  ) => {
    try {
      await userPreferenceService.updatePreference(userId, key, value);
      // Fetch updated preferences
      const preferences = await userPreferenceService.getFullUserPreferences(userId);
      return preferences;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update preference'
      );
    }
  }
);

// Update multiple preferences at once
export const updateMultiplePreferences = createAsyncThunk(
  'userPreferences/updateMultiple',
  async (
    {
      userId,
      updates,
    }: {
      userId: number;
      updates: Partial<UserPreferences>;
    },
    { rejectWithValue }
  ) => {
    try {
      const preferences = await userPreferenceService.updateMultiplePreferences(
        userId,
        updates
      );
      return preferences;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update preferences'
      );
    }
  }
);

// Reset preferences to system defaults
export const resetPreferences = createAsyncThunk(
  'userPreferences/reset',
  async (userId: number, { rejectWithValue }) => {
    try {
      const preferences = await userPreferenceService.resetToDefaults(userId);
      return preferences;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to reset preferences'
      );
    }
  }
);

/**
 * User Preferences Slice
 */
const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    // Clear preferences (useful for logout)
    clearPreferences: (state) => {
      state.preferences = null;
      state.isLoading = false;
      state.isSaving = false;
      state.error = null;
      state.lastUpdated = null;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Set preferences directly (useful for initialization)
    setPreferences: (state, action: PayloadAction<UserPreferences>) => {
      state.preferences = action.payload;
      state.lastUpdated = Date.now();
    },
  },
  extraReducers: (builder) => {
    // Fetch user preferences
    builder
      .addCase(fetchUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
        state.lastUpdated = Date.now();
        syncPreferenceSideEffects(action.payload);
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update single preference
    builder
      .addCase(updateUserPreference.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateUserPreference.fulfilled, (state, action) => {
        state.isSaving = false;
        state.preferences = action.payload;
        state.lastUpdated = Date.now();
        syncPreferenceSideEffects(action.payload);
      })
      .addCase(updateUserPreference.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    // Update multiple preferences
    builder
      .addCase(updateMultiplePreferences.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateMultiplePreferences.fulfilled, (state, action) => {
        state.isSaving = false;
        state.preferences = action.payload;
        state.lastUpdated = Date.now();
        syncPreferenceSideEffects(action.payload);
      })
      .addCase(updateMultiplePreferences.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });

    // Reset preferences
    builder
      .addCase(resetPreferences.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(resetPreferences.fulfilled, (state, action) => {
        state.isSaving = false;
        state.preferences = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(resetPreferences.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearPreferences, clearError, setPreferences } = userPreferencesSlice.actions;

// Export reducer
export default userPreferencesSlice.reducer;

// Selectors
export const selectUserPreferences = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.preferences;

export const selectIsLoadingPreferences = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.isLoading;

export const selectIsSavingPreferences = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.isSaving;

export const selectPreferencesError = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.error;

export const selectLastUpdated = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.lastUpdated;

// Notification-specific selectors
export const selectNotificationSoundEnabled = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.preferences?.notificationSoundEnabled ?? true;

export const selectNotificationSoundVolume = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.preferences?.notificationSoundVolume ?? 80;

export const selectNotificationToastEnabled = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences.preferences?.notificationToastEnabled ?? true;
